/**
 * Data Exceptions service - cross-FP dashboard aggregates (Exceptions Hub).
 *
 * Backs the Dashboard landing at /admin/data-exceptions. Mirrors the response
 * models in backend/api/routes/admin/data_exceptions.py. All current-state
 * counts speak the 4-status canonical model so the numbers match Triage.
 */
import { apiClient } from '../api'

export interface DataExceptionScope {
  market?: string
  region?: string
  fp_code?: string
  fp_status?: string
}

export interface DomainOpenCount {
  data_domain: string
  open: number
}

export interface ResolutionAttribution {
  after_evo_action: number
  after_fp_action: number // Phase 1: always 0 (FP writes deferred) - not shown in UI
  silent: number
}

export interface ExceptionSummary {
  needs_review: number
  blocked: number
  resolved_last_30d: number
  ignored_total: number
  aging_30d_plus: number
  in_discussion: number
  avg_resolve_hours: number | null
  prior_period_resolved: number
  prior_period_avg_resolve_hours: number | null
  by_domain: DomainOpenCount[]
  resolution_attribution: ResolutionAttribution
}

// ---- Cadence buckets (Overview cadence cards) ----

export type CadenceBucketKey = 'due_today' | 'this_week' | 'this_month' | 'backlog'

export interface CadenceBucket {
  key: CadenceBucketKey
  total: number
  reviewed: number
  not_looked: number
}

export interface CadenceBucketsResponse {
  buckets: CadenceBucket[]
}

// ---- Trending (rate-of-change early warning) ----

export type TrendingWindow = '24h' | '48h' | '7d'
export type TrendingMetric = 'new' | 'blocks' | 'notes'

export interface TrendingRow {
  key: string // fp_code (by_fp) or exception code (by_type)
  label: string
  market: string | null
  new_window: number
  new_prior: number
  delta: number
  new_blocks_window: number
  notes_window: number
  open_total: number
}

export interface TrendingResponse {
  window: TrendingWindow
  metric: TrendingMetric
  by_fp: TrendingRow[]
  by_type: TrendingRow[]
}

// ---- FP Grid (franchise x exception-type heat matrix) ----

export type GridMetric = 'needs_review' | 'open' | 'blocked'

export interface GridDomain {
  key: string
  label: string
}

export interface GridExceptionType {
  code: string
  name: string
  domain: string
}

export interface GridRow {
  fp_code: string
  franchise_name: string
  market: string | null
  counts: Record<string, number>
  total: number
}

export interface GridResponse {
  metric: GridMetric
  domains: GridDomain[]
  exception_types: GridExceptionType[]
  rows: GridRow[]
  totals: Record<string, number>
  grand_total: number
}

// `compare_unit` granularity for the Report Builder matrix job.
export type TrendGranularity = 'day' | 'week' | 'month'

export type ExceptionStatusSlice =
  | 'open'
  | 'needs_review'
  | 'blocked'
  | 'resolved'
  | 'ignored'

export interface DomainExceptionType {
  code: string
  name: string
}

// ---- Report Builder (matrix + comparison job) ----

export interface DeltaCell {
  current: number
  prior: number
  delta: number
  snapshots: number[]
}

export interface ReportFilters {
  market?: string
  region?: string
  fp_status?: string
  fp_code?: string
  data_domains?: string[]
  exception_codes?: string[]
  status?: ExceptionStatusSlice
  compare?: boolean
  compare_unit?: TrendGranularity
  compare_periods?: number
}

export interface ReportSummary {
  status: string
  compare: boolean
  compare_unit: string
  snapshots: string[]
  exception_types: DomainExceptionType[]
  column_totals: Record<string, DeltaCell>
  grand_total: DeltaCell
  [key: string]: unknown
}

export interface ReportMatrixRow {
  fp_code: string
  franchise_name: string
  country: string
  cells: Record<string, DeltaCell>
  total: DeltaCell
}

export interface ReportJob {
  job_id: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress_percentage: number
  progress_message: string | null
  result_summary: ReportSummary | null
  result_data: ReportMatrixRow[] | null
  created_at: string | null
  completed_at: string | null
  error_message: string | null
}

class DataExceptionsApi {
  async getSummary(scope: DataExceptionScope = {}): Promise<ExceptionSummary> {
    const r = await apiClient.get<ExceptionSummary>(
      '/admin/data-exceptions/summary',
      { params: scope },
    )
    return r.data
  }

  async getCadenceBuckets(
    scope: DataExceptionScope = {},
  ): Promise<CadenceBucketsResponse> {
    const r = await apiClient.get<CadenceBucketsResponse>(
      '/admin/data-exceptions/cadence-buckets',
      { params: scope },
    )
    return r.data
  }

  async getTrending(
    scope: DataExceptionScope & {
      window?: TrendingWindow
      metric?: TrendingMetric
    } = {},
  ): Promise<TrendingResponse> {
    const r = await apiClient.get<TrendingResponse>(
      '/admin/data-exceptions/trending',
      { params: scope },
    )
    return r.data
  }

  async getGrid(
    scope: DataExceptionScope & { metric?: GridMetric } = {},
  ): Promise<GridResponse> {
    const r = await apiClient.get<GridResponse>(
      '/admin/data-exceptions/grid',
      { params: scope },
    )
    return r.data
  }

  async submitReport(filters: ReportFilters): Promise<{ job_id: string }> {
    const r = await apiClient.post<{ job_id: string }>(
      '/admin/data-exceptions/report',
      { filters },
    )
    return r.data
  }

  async getReport(jobId: string): Promise<ReportJob> {
    const r = await apiClient.get<ReportJob>(
      `/admin/data-exceptions/report/${jobId}`,
    )
    return r.data
  }

  reportDownloadUrl(jobId: string): string {
    return `/api/admin/data-exceptions/report/${jobId}/download`
  }
}

export const dataExceptionsApi = new DataExceptionsApi()
